<template>
    <div class="settings-container container">
      <el-card shadow="hover">
        <template #header>
          <div class="card-header">
            <h2><el-icon><Setting /></el-icon> إعدادات الجدول الزمني الدراسي</h2>
          </div>
        </template>
  
        <el-alert
          title="كيف تعمل هذه الصفحة؟"
          type="info"
          description="من هنا يمكنك التحكم الكامل في الفترات الزمنية لليوم الدراسي. قم بإدارة جميع الحصص والفسح وحفظها دفعة واحدة. يمكنك البدء من الصفر أو استخدام القالب الافتراضي كنقطة انطلاق."
          :closable="false"
          show-icon
          style="margin-bottom: 20px;"
        />
  
        <div class="toolbar">
          <el-button type="primary" @click="showManageDialog">
            <el-icon><EditPen /></el-icon> إدارة الفترات الزمنية
          </el-button>
        </div>
  
        <el-table :data="schedulePeriods" v-loading="loading" border style="width: 100%; margin-top: 20px;">
          <el-table-column prop="name" label="اسم الفترة" />
          <el-table-column prop="start_time" label="وقت البدء" width="120" />
          <el-table-column prop="end_time" label="وقت الانتهاء" width="120" />
          <el-table-column label="النوع" width="120">
            <template #default="{ row }">
              <el-tag :type="row.is_break ? 'info' : 'success'">
                {{ row.is_break ? 'فسحة' : 'حصة دراسية' }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
  
      <el-dialog v-model="dialogVisible" title="إدارة الفترات الزمنية" width="60%" :close-on-click-modal="false">
        <div class="dialog-toolbar">
          <el-button type="success" @click="loadDefaultTemplate" plain>
            <el-icon><Download /></el-icon> استخدام القالب الافتراضي
          </el-button>
        </div>
        
        <el-form :model="form" ref="formRef" label-position="top">
          <div v-for="(period, index) in form.periods" :key="index" class="period-row">
            <el-row :gutter="15">
              <el-col :span="8">
                <el-form-item label="اسم الفترة" :prop="'periods.' + index + '.name'" :rules="{ required: true, message: 'الاسم مطلوب', trigger: 'blur' }">
                  <el-input v-model="period.name" />
                </el-form-item>
              </el-col>
              <el-col :span="5">
                <el-form-item label="وقت البدء" :prop="'periods.' + index + '.start_time'" :rules="{ required: true, message: 'مطلوب', trigger: 'change' }">
                  <el-time-picker v-model="period.start_time" format="HH:mm" value-format="HH:mm:ss" style="width: 100%;"/>
                </el-form-item>
              </el-col>
              <el-col :span="5">
                <el-form-item label="وقت الانتهاء" :prop="'periods.' + index + '.end_time'" :rules="{ required: true, message: 'مطلوب', trigger: 'change' }">
                  <el-time-picker v-model="period.end_time" format="HH:mm" value-format="HH:mm:ss" style="width: 100%;"/>
                </el-form-item>
              </el-col>
              <el-col :span="4" class="action-col">
                <el-switch v-model="period.is_break" active-text="فسحة" style="margin-right: 15px;"/>
                <el-button @click="removePeriodFromForm(index)" type="danger" circle :icon="Delete" />
              </el-col>
            </el-row>
          </div>
        </el-form>
        
        <el-button @click="addPeriodToForm" type="primary" plain style="width: 100%; margin-top: 10px;">
          <el-icon><Plus /></el-icon> إضافة فترة جديدة
        </el-button>
        
        <template #footer>
          <el-button @click="dialogVisible = false">إلغاء</el-button>
          <el-button type="primary" @click="submitForm" :loading="formLoading">حفظ كل التغييرات</el-button>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { ElMessage } from 'element-plus';
  import { Setting, Plus, Download, EditPen, Delete } from '@element-plus/icons-vue';
  import { cloneDeep } from 'lodash'; // A reliable way to deep copy objects
  
  export default {
    name: 'ScheduleSettings',
    components: { Setting, Plus, Download, EditPen, Delete },
    data() {
      return {
        api: null,
        loading: true,
        schedulePeriods: [],
        dialogVisible: false,
        formLoading: false,
        form: { periods: [] }, // Form now holds an array of periods
        defaultPeriods: [
          { name: 'الحصة الأولى', start_time: '08:00:00', end_time: '08:45:00', is_break: false },
          { name: 'الحصة الثانية', start_time: '08:45:00', end_time: '09:30:00', is_break: false },
          { name: 'الفسحة الأولى', start_time: '09:30:00', end_time: '09:45:00', is_break: true },
          { name: 'الحصة الثالثة', start_time: '09:45:00', end_time: '10:30:00', is_break: false },
          { name: 'الحصة الرابعة', start_time: '10:30:00', end_time: '11:15:00', is_break: false },
          { name: 'فسحة الغداء', start_time: '11:15:00', end_time: '12:00:00', is_break: true },
          { name: 'الحصة الخامسة', start_time: '12:00:00', end_time: '12:45:00', is_break: false },
          { name: 'الحصة السادسة', start_time: '12:45:00', end_time: '13:30:00', is_break: false },
        ],
      };
    },
    created() {
      this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
      this.api.interceptors.request.use((config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      });
      this.fetchPeriods();
    },
    methods: {
      async fetchPeriods() {
        this.loading = true;
        try {
          const response = await this.api.get('/schedule-periods');
          this.schedulePeriods = response.data;
        } catch (error) {
          ElMessage.error('فشل في جلب الفترات الزمنية.');
        } finally {
          this.loading = false;
        }
      },
      showManageDialog() {
        // Load current periods into the form for editing
        this.form.periods = cloneDeep(this.schedulePeriods); 
        this.dialogVisible = true;
      },
      // ⭐ يملأ الفورم بالبيانات الافتراضية
      loadDefaultTemplate() {
        this.form.periods = cloneDeep(this.defaultPeriods);
        ElMessage.info('تم تحميل القالب الافتراضي. يمكنك التعديل عليه الآن قبل الحفظ.');
      },
      addPeriodToForm() {
        this.form.periods.push({ name: '', start_time: '', end_time: '', is_break: false });
      },
      removePeriodFromForm(index) {
        this.form.periods.splice(index, 1);
      },
      async submitForm() {
        if (!this.$refs.formRef) return;
        await this.$refs.formRef.validate(async (valid) => {
          if (!valid) {
            ElMessage.error('الرجاء التأكد من تعبئة جميع الحقول المطلوبة.');
            return;
          }
          this.formLoading = true;
          try {
            // Send the entire array to a new 'sync' endpoint
            await this.api.post('/schedule-periods/sync', { periods: this.form.periods });
            ElMessage.success('تم حفظ الإعدادات بنجاح!');
            this.dialogVisible = false;
            this.fetchPeriods(); // Refresh the main table
          } catch (error) {
            ElMessage.error(error.response?.data?.message || 'فشلت عملية الحفظ.');
          } finally {
            this.formLoading = false;
          }
        });
      },
    },
  };
  </script>
  
  <style scoped>
  .settings-container { padding: 20px; }
  .card-header { display: flex; justify-content: space-between; align-items: center; }
  .toolbar { display: flex; gap: 10px; margin-bottom: 20px; }
  .dialog-toolbar { margin-bottom: 20px; }
  .period-row {
    display: flex;
    align-items: center;
    border-bottom: 1px solid #e4e7ed;
    padding-bottom: 10px;
    margin-bottom: 10px;
  }
  .action-col {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-top: 30px; /* Align with form inputs */
  }
  </style>